import {
  Badge,
  Body,
  Heading,
  Icon,
  TableData,
} from "@elaniintc/goes-design-system";
import { tableColumns } from "./form-detail-table";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  useGetFormalityRequestDetail,
  useGetFormDetail2,
} from "@/queries/formality-request";
import {
  FORM_ASSIGNMENTS_STATUS,
  type FormDetailResponse,
  type FormType,
} from "@/types/entities/formality-request";
import { useCallback, useEffect, useMemo, useState } from "react";
import DenyReasonShowModal from "./deny-reason-show-modal";
import FieldHistoryModal from "./field-history-modal";
import { cn } from "@/utils/theme";
import {
  formatCommentText,
  getLastRevisionComments,
} from "@/utils/procedures-ui";
import { useQueryState } from "nuqs";

type StepType = FormDetailResponse["steps"][number];
type SectionType = StepType["sections"][number];
type FieldType = SectionType["fields"][number];

function filterFieldsByAssignment(
  fields: FieldType[],
  assignmentId: string,
): FieldType[] {
  return fields.map((f) => ({
    ...f,
    filled_fields:
      f.filled_fields?.filter(
        (filled) => filled.filled_form_id === assignmentId,
      ) || [],
  }));
}

function hasFilledFields(fields: FieldType[], assignmentId: string): boolean {
  return fields.some((f) =>
    f.filled_fields?.some((filled) => filled.filled_form_id === assignmentId),
  );
}

function buildVisibleStepsForDetail(
  data: FormDetailResponse,
  formAssignment: string,
  filterVisible: (fields: FieldType[]) => FieldType[],
) {
  let globalSectionIndex = 0;
  let globalFieldIndex = 0;

  return data.steps
    ?.map((step) => {
      const visibleSections = step.sections
        .filter((section) => hasFilledFields(section.fields, formAssignment))
        .map((s) => ({
          ...s,
          fields: filterFieldsByAssignment(s.fields, formAssignment),
        }));
      return { step, visibleSections };
    })
    .filter(({ visibleSections }) => visibleSections.length > 0)
    .map(({ step, visibleSections }, visibleStepIndex) => {
      const sectionStartIndex = globalSectionIndex;
      const fieldStartIndex = globalFieldIndex;

      for (const section of visibleSections) {
        globalSectionIndex++;
        globalFieldIndex += filterVisible(section.fields).length;
      }

      return {
        step: {
          ...step,
          sections: step.sections.map((s) => ({
            ...s,
            fields: filterFieldsByAssignment(s.fields, formAssignment),
          })),
        },
        stepNumber: visibleStepIndex + 1,
        visibleSections,
        sectionStartIndex,
        fieldStartIndex,
      };
    });
}

type AssignmentType =
  | FormDetailResponse["form_assignments"][number]
  | FormDetailResponse["user_form_assignments"][number];

const filterVisibleFields = (
  fields: FormDetailResponse["steps"][number]["sections"][number]["fields"],
) =>
  fields.filter((field) => {
    const isConditionalField = (field as { is_conditional?: boolean })
      .is_conditional;
    if (
      isConditionalField &&
      (!field.filled_fields || field.filled_fields.length === 0)
    ) {
      return false;
    }
    return true;
  });

const SectionList = ({
  stepTitle,
  stepNumber,
  data,
  assigment,
  applicant,
  sectionStartIndex,
  fieldStartIndex,
}: {
  stepTitle: string;
  stepNumber: number;
  data: FormDetailResponse["steps"][number]["sections"];
  assigment?: AssignmentType;
  applicant?: {
    fullName: string;
    date?: string | null;
  };
  sectionStartIndex: number;
  fieldStartIndex: number;
}) => {
  const [denyReason, setDenyReason] = useState<string>("");
  const [fieldSelected, setFieldSelected] = useState<FieldType | null>(null);

  const onShowFieldHistory = useCallback((field: FieldType) => {
    setFieldSelected(field);
  }, []);

  const sectionsWithData = useMemo(() => {
    let fieldCount = 0;
    return data.map((section, localIndex) => {
      const visibleFields = filterVisibleFields(section.fields);
      const sectionFieldStart = fieldStartIndex + fieldCount;
      fieldCount += visibleFields.length;
      return {
        section,
        globalSectionIndex: sectionStartIndex + localIndex + 1,
        visibleFields,
        sectionFieldStart,
      };
    });
  }, [data, sectionStartIndex, fieldStartIndex]);

  const allSectionColumns = useMemo(() => {
    return sectionsWithData.map(({ sectionFieldStart }) =>
      tableColumns(
        assigment as FormDetailResponse["form_assignments"][number],
        (reason: string) => setDenyReason(reason),
        sectionFieldStart,
        onShowFieldHistory,
      ),
    );
  }, [sectionsWithData, assigment, onShowFieldHistory]);

  return (
    <div className="border-[0.5px] border-neutral-quiet rounded-2xl overflow-hidden">
      <div className="p-3 bg-neutral-object border-b border-neutral-quiet flex items-center justify-between gap-2">
        <Heading variant="h6">{stepTitle}</Heading>
        <Badge
          variant="success"
          shape="pill"
          fill="ghost"
          size="sm"
          className="shrink-0"
        >
          Paso {stepNumber}
        </Badge>
      </div>
      {sectionsWithData.map(
        ({ section, globalSectionIndex, visibleFields }, localIndex) => {
          return (
            <div key={section.id}>
              <div className="flex items-center justify-between py-2.5 px-3 bg-neutral-object-accent-silent">
                <h6 className="font-semibold text-body-loud text-xs uppercase">
                  {section.title}
                </h6>
                <Badge
                  variant="action"
                  shape="pill"
                  fill="ghost"
                  size="sm"
                  className="shrink-0"
                >
                  Sección {globalSectionIndex}
                </Badge>
              </div>

              <TableData
                columns={allSectionColumns[localIndex]}
                data={visibleFields}
                stripped
                emptyState={<span></span>}
                wrapperClassName={cn(
                  "[&_thead]:hidden overflow-x-hidden",
                  localIndex === sectionsWithData.length - 1 &&
                    "rounded-b-2xl shadow-custom",
                )}
              />
            </div>
          );
        },
      )}
      {denyReason && (
        <DenyReasonShowModal
          isOpen={!!denyReason}
          onAccept={() => setDenyReason("")}
          onClose={() => setDenyReason("")}
          defaultReason={denyReason}
        />
      )}
      {fieldSelected && (
        <FieldHistoryModal
          isOpen={!!fieldSelected}
          applicant={applicant}
          onClose={() => setFieldSelected(null)}
          formAssigment={
            assigment as FormDetailResponse["form_assignments"][number]
          }
          fieldSelected={fieldSelected}
        />
      )}
    </div>
  );
};

export const FormDetail = () => {
  const params = useParams<{ formId: string; procedureId: string }>();
  const [formAssignment] = useQueryState("assigment", { defaultValue: "" });
  const navigate = useNavigate();
  const location = useLocation();

  const { data: formalityRequest } = useGetFormalityRequestDetail(
    params.procedureId ?? "",
  );

  const { data, isPending, isLoading, isFetching } = useGetFormDetail2(
    params.formId ?? "",
    params.procedureId ?? "",
  );

  // Guard: si la data cargó pero no hay assignments, la card estaba bloqueada
  useEffect(() => {
    // Esperar a que finalice cualquier carga/refetch antes de evaluar
    if (isPending || isLoading || isFetching || !data) return;
    const hasFormAssignments =
      data.form_assignments && data.form_assignments.length > 0;
    const hasUserFormAssignments =
      data.user_form_assignments && data.user_form_assignments.length > 0;
    if (!hasFormAssignments && !hasUserFormAssignments) {
      const isExpedientes = location.pathname.includes("/expedientes/");
      const backUrl = isExpedientes
        ? location.pathname.replace(/\/[^/]+$/, "") // quita el /:formId final
        : `/tramite/${params.procedureId}`;
      navigate(backUrl, { replace: true });
    }
  }, [
    data,
    isPending,
    isLoading,
    isFetching,
    navigate,
    params.procedureId,
    location.pathname,
  ]);

  const activeAssignments = useMemo(() => {
    if (!data) return [];

    if (data.form_assignments && data.form_assignments.length > 0) {
      return data.form_assignments;
    }
    return data.user_form_assignments || [];
  }, [data]);

  const latestAssignment = useMemo(() => {
    if (formAssignment) {
      return activeAssignments.find(
        (assignment) => assignment.filled_form_id === formAssignment,
      );
    }
    return activeAssignments[0];
  }, [formAssignment, activeAssignments]);

  const revisionComment = useMemo(() => {
    return getLastRevisionComments(data as FormType);
  }, [data]);

  const applicant = useMemo(
    () => ({
      fullName:
        `${formalityRequest?.applicant?.first_name ?? ""} ${formalityRequest?.applicant?.last_name ?? ""}`.trim(),
      date: formalityRequest?.updated_at || formalityRequest?.created_at,
    }),
    [formalityRequest],
  );

  const visibleStepsWithIndices = useMemo(() => {
    if (!data) return [];
    return buildVisibleStepsForDetail(
      data,
      latestAssignment?.filled_form_id ?? "",
      filterVisibleFields,
    );
  }, [data, latestAssignment]);
  const isExpired = useMemo(() => {
    // get the latest revision by created date
    const latestRevision = latestAssignment?.revisions?.reduce(
      (latest, current) =>
        new Date(latest.created_at || "") > new Date(current.created_at || "")
          ? latest
          : current,
      latestAssignment?.revisions[0],
    );
    if (!latestRevision) return false;
    return latestRevision.status === "EXPIRED";
  }, [latestAssignment]);

  if (isPending || isLoading || isFetching || !data) {
    return (
      <div>
        <Icon
          name="spinner-third"
          type="fas"
          className="text-on-disabled animate-spin"
        />
      </div>
    );
  }

  if (formalityRequest?.status?.toUpperCase() === "CANCELED") {
    navigate("/403");
    return null;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between items-center">
        <div>
          <Heading variant="h6">{data.name}</Heading>
          <Body>Revisa las respuestas del formulario.</Body>
        </div>

        {latestAssignment?.status ===
          FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION && (
          <Badge size="xs" variant="warning">
            Observado
            <Icon type="fas" name="file-edit" className="text-warning-quiet" />
          </Badge>
        )}

        {latestAssignment?.status ===
          FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION && (
          <Badge size="xs" variant="danger">
            Rechazado
            <Icon type="fas" name="times" className="text-danger-quiet" />
          </Badge>
        )}
      </div>

      {revisionComment &&
        latestAssignment?.status !== FORM_ASSIGNMENTS_STATUS.COMPLETED &&
        latestAssignment?.status !== FORM_ASSIGNMENTS_STATUS.SUBMITTED && (
          <div className="bg-surface-warning-accent-quiet border rounded-lg border-contextual-warning-quiet p-2 flex flex-col gap-1">
            <Heading className="text-size-body-sm leading-2xl">
              Comentario general
            </Heading>
            <p className="font-body text-body text-size-body whitespace-pre-line text-justify wrap-break-word min-w-0">
              {formatCommentText(revisionComment)}
            </p>
          </div>
        )}

      {isExpired && (
        <div
          data-testid="expired-revision-banner"
          className="bg-surface-warning-accent-quiet border rounded-lg border-contextual-warning-quiet p-2 flex items-center gap-1"
        >
          <Icon name="clock" type="fas" className="text-warning-quiet" />
          <p className="font-body text-body text-size-body whitespace-pre-line text-justify wrap-break-word min-w-0">
            El temporizador de esta revisión ha caducado
          </p>
        </div>
      )}

      {/* steps */}
      <div className="flex flex-col gap-6">
        {visibleStepsWithIndices.map(
          ({
            step,
            stepNumber,
            visibleSections,
            sectionStartIndex,
            fieldStartIndex,
          }) => (
            <SectionList
              key={step.id}
              stepTitle={step.name}
              stepNumber={stepNumber}
              data={visibleSections}
              assigment={latestAssignment}
              applicant={applicant}
              sectionStartIndex={sectionStartIndex}
              fieldStartIndex={fieldStartIndex}
            />
          ),
        )}
      </div>
    </div>
  );
};
